import { Box, Flex, Grid, GridItem, Show } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftMessage from '@/components/Atoms/FaceliftMessage';
import { useUpdateUserProfile } from '@/hooks/service-hooks/users';
import { selectUser, useAuthStore } from '@/store/authStore';
import { fileToBase64, getDisplayableImageUrl } from '@/utils';
import Information from './Information';
import ProfileCard from './ProfilePictureCard';
import { portBasedApplication } from '@/constants';

export interface ProfileFormData {
	firstName: string;
	lastName: string;
	email: string;
	role: string;
	profileImage: string;
}

const profileSchema: yup.ObjectSchema<ProfileFormData> = yup.object().shape({
	firstName: yup
		.string()
		.test('no-leading-spaces', 'No leading spaces allowed',
			value => !value || value === value.trimStart())
		.required('First name is required')
		.min(2, 'First name must be at least 2 characters')
		.max(50, 'First name must not exceed 50 characters'),
	lastName: yup
		.string()
		.test('no-leading-spaces', 'No leading spaces allowed',
			value => !value || value === value.trimStart())
		.required('Last name is required')
		.min(2, 'Last name must be at least 2 characters')
		.max(50, 'Last name must not exceed 50 characters'),
	email: yup.string().optional().default(''),
	role: yup.string().optional().default(''),
	profileImage: yup.string().optional().default(''),
});

const General = () => {
	const [profileSuccessMessage, setProfileSuccessMessage] = useState('');
	const [passwordSuccessMessage, setPasswordSuccessMessage] = useState('');
	const [hasImageChanged, setHasImageChanged] = useState(false);
	const user = useAuthStore(selectUser);
	const userInformation = user?.userInformation;
	const authStore = useAuthStore.getState();
	const {
		control,
		setValue,
		getValues,
		handleSubmit,
		formState: { errors, isSubmitting, isDirty },
	} = useForm<ProfileFormData>({
		resolver: yupResolver(profileSchema),
		defaultValues: {
			firstName: userInformation?.firstName || '',
			lastName: userInformation?.lastName || '',
			email: userInformation?.email || '',
			role: userInformation?.role || '',
			profileImage: userInformation?.profileImage || '',
		},
		mode: 'onBlur',
	});

	const {
		mutateAsync: updateUserProfile,
		isLoading: updateLoading,
		isSuccess: updateSuccess,
	} = useUpdateUserProfile(
		(response) => {
			const { data, message } = response;
			setProfileSuccessMessage(message);
			authStore.updateUserInformation({
				firstName: data.firstName,
				lastName: data.lastName,
				profileImage: data.profileImage,
			});
			setValue('firstName', data.firstName || '');
			setValue('lastName', data.lastName || '');
			if (data.profileImage) {
				setValue('profileImage', data.profileImage || '');
			}
			setHasImageChanged(false);
		},
		(error) => {
			const apiError = error as { response?: { data?: { message?: string } } };
			toast.error(
				apiError?.response?.data?.message ||
				'Failed to update user profile. Please try again.',
			);
		},
	);

	const handleAvatarChange = async (file: File) => {
		try {
			const base64 = await fileToBase64(file);
			setValue('profileImage', base64);
			setHasImageChanged(true);
		} catch (error) {
			toast.error(
				(error as Error).message ||
				'Failed to process image. Please try again.',
			);
		}
	};

	const handleRemovePhoto = () => {
		setValue('profileImage', '');
		setHasImageChanged(true);
	};

	const onSubmit = async (data: ProfileFormData) => {
		try {
			const payload = {
				firstName: data.firstName.trim(),
				lastName: data.lastName.trim(),
				profileImage: data.profileImage || '',
			};
			await updateUserProfile({
				id: userInformation?._id || '',
				data: payload,
			});
		} catch (error) {
			const apiError = error as { response?: { data?: { message?: string } } };
			toast.error(
				apiError?.response?.data?.message ||
				'Failed to update user. Please try again.',
			);
		}
	};

	const handlePasswordUpdateSuccess = (message: string) => {
		setPasswordSuccessMessage(message);
	};

	return (
		<>
			<Box width="100%" as="form" onSubmit={handleSubmit(onSubmit)}>
				<Grid
					templateColumns={{
						base: '1fr',
						md: '330px 1fr',
					}}
					rowGap={6}
					alignItems="end"
				>
					<GridItem>
						<ProfileCard
							name={
								`${userInformation?.firstName || ''} ${userInformation?.lastName || ''}`.trim() ||
								'-'
							}
							designation={userInformation?.role || '-'}
							company={portBasedApplication?.[userInformation?.portsAvailable as keyof typeof portBasedApplication || ''] || '-'}
							avatarUrl={
								getValues('profileImage')
									? getDisplayableImageUrl(getValues('profileImage'))
									: getDisplayableImageUrl(userInformation?.profileImage)
							}
							onAvatarChange={handleAvatarChange}
							onRemovePhoto={handleRemovePhoto}
							onDownloadPhoto={() => {
								const imageUrl =
									getValues('profileImage') || userInformation?.profileImage;
								if (imageUrl) {
									const link = document.createElement('a');
									link.href = getDisplayableImageUrl(imageUrl) || '';
									link.download = `${userInformation?.firstName}_${userInformation?.lastName}_profile.jpg`;
									link.click();
								}
							}}
						/>
					</GridItem>

					<GridItem>
						<Information
							control={control}
							errors={errors}
							email={userInformation?.email || ''}
							onPasswordUpdateSuccess={handlePasswordUpdateSuccess}
						/>
					</GridItem>
				</Grid>

				<Flex justify="flex-end" mt={4}>
					<FaceliftButton
						width="250px"
						size="md"
						type="submit"
						loading={isSubmitting || updateLoading}
						disabled={
							(!isDirty && !hasImageChanged) || isSubmitting || updateLoading
						}
					>
						Update
					</FaceliftButton>
				</Flex>
			</Box>

			<Show when={updateSuccess}>
				<FaceliftMessage message={profileSuccessMessage} />
			</Show>

			<Show when={passwordSuccessMessage}>
				<FaceliftMessage messageTitle='Password Updated' message={passwordSuccessMessage} />
			</Show>
		</>
	);
};

export default General;
